import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Palette, Cloud, Zap, ExternalLink, Plus, Trash2, 
  CheckCircle, AlertCircle, Settings, Copy, Play,
  FileText, Image, BarChart, Mail, Bell, RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function IntegrationsPage() {
  const [activeTab, setActiveTab] = useState("canva");
  const [designTypes, setDesignTypes] = useState([]);
  const [exportFormats, setExportFormats] = useState([]);
  const [zapierEvents, setZapierEvents] = useState([]);
  const [userWebhooks, setUserWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Canva state
  const [selectedDesignType, setSelectedDesignType] = useState("");
  const [designTitle, setDesignTitle] = useState("");
  
  // Google Drive state
  const [exportContent, setExportContent] = useState("");
  const [exportFilename, setExportFilename] = useState("markeemix-export");
  const [exportFormat, setExportFormat] = useState("txt");
  
  // Zapier state
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookEvent, setWebhookEvent] = useState("");

  useEffect(() => {
    fetchIntegrationData();
  }, []);

  const fetchIntegrationData = async () => {
    try {
      const [designTypesRes, formatsRes, eventsRes, webhooksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/integrations/canva/design-types`),
        axios.get(`${BACKEND_URL}/api/integrations/google-drive/export-formats`),
        axios.get(`${BACKEND_URL}/api/integrations/zapier/events`),
        axios.get(`${BACKEND_URL}/api/integrations/zapier/webhooks`)
      ]);
      
      setDesignTypes(designTypesRes.data.design_types || []);
      setExportFormats(formatsRes.data.formats || []);
      setZapierEvents(eventsRes.data.events || []);
      setUserWebhooks(webhooksRes.data.webhooks || []);
    } catch (error) {
      console.error("Error fetching integration data:", error);
    }
  };

  // Canva handlers
  const handleCreateCanvaDesign = async () => {
    if (!selectedDesignType) {
      toast.error("Please select a design type");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/integrations/canva/create`, {
        design_type: selectedDesignType,
        title: designTitle || undefined
      });
      
      window.open(response.data.canva_url, "_blank");
      toast.success(`Opening Canva for ${response.data.name}`);
    } catch (error) {
      toast.error("Failed to create Canva design");
    }
    setLoading(false);
  };

  // Google Drive handlers
  const handlePrepareExport = async () => {
    if (!exportContent.trim()) {
      toast.error("Please enter content to export");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/integrations/google-drive/prepare-export`, {
        content: exportContent,
        filename: exportFilename,
        file_type: exportFormat
      });
      
      // Download locally for now
      const blob = new Blob([response.data.content], { type: response.data.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${response.data.filename}`);
    } catch (error) {
      toast.error("Failed to prepare export");
    }
    setLoading(false);
  };

  // Zapier handlers
  const handleConfigureWebhook = async () => {
    if (!webhookUrl || !webhookEvent) {
      toast.error("Please enter webhook URL and select event");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/integrations/zapier/webhook/configure`, {
        webhook_url: webhookUrl,
        event_type: webhookEvent,
        is_active: true
      });
      
      toast.success(`Webhook configured for ${webhookEvent}`);
      setWebhookUrl("");
      setWebhookEvent("");
      fetchIntegrationData();
    } catch (error) {
      toast.error("Failed to configure webhook");
    }
    setLoading(false);
  };

  const handleDeleteWebhook = async (eventType) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/integrations/zapier/webhook/${eventType}`);
      toast.success("Webhook deleted");
      fetchIntegrationData();
    } catch (error) {
      toast.error("Failed to delete webhook");
    }
  };

  const handleTestWebhook = async (eventType) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/integrations/zapier/test-webhook`, {
        event_type: eventType
      });
      
      if (response.data.webhook_results?.some(r => r.success)) {
        toast.success("Test webhook sent successfully!");
      } else {
        toast.warning("Webhook sent but no active webhooks found");
      }
    } catch (error) {
      toast.error("Failed to test webhook");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
              <Zap className="w-3 h-3 mr-1" />
              Power Up Your Workflow
            </Badge>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Integrations</h1>
            <p className="text-gray-600">Connect MarkeeMix with your favorite tools</p>
          </div>

          {/* Integration Cards Overview */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className={`cursor-pointer transition-all ${activeTab === 'canva' ? 'ring-2 ring-purple-500' : ''}`}
                  onClick={() => setActiveTab('canva')}>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <Palette className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-1">Canva</h3>
                <p className="text-sm text-gray-600">Create stunning designs</p>
                <Badge className="mt-2 bg-green-100 text-green-700 border-0">Connected</Badge>
              </CardContent>
            </Card>

            <Card className={`cursor-pointer transition-all ${activeTab === 'google-drive' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setActiveTab('google-drive')}>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Cloud className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-1">Google Drive</h3>
                <p className="text-sm text-gray-600">Export & save to cloud</p>
                <Badge className="mt-2 bg-green-100 text-green-700 border-0">Connected</Badge>
              </CardContent>
            </Card>

            <Card className={`cursor-pointer transition-all ${activeTab === 'zapier' ? 'ring-2 ring-orange-500' : ''}`}
                  onClick={() => setActiveTab('zapier')}>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-1">Zapier</h3>
                <p className="text-sm text-gray-600">Connect 5000+ apps</p>
                <Badge className="mt-2 bg-blue-100 text-blue-700 border-0">
                  {userWebhooks.length} Active
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Tab Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="hidden">
              <TabsTrigger value="canva">Canva</TabsTrigger>
              <TabsTrigger value="google-drive">Google Drive</TabsTrigger>
              <TabsTrigger value="zapier">Zapier</TabsTrigger>
            </TabsList>

            {/* Canva Tab */}
            <TabsContent value="canva">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-purple-500" />
                    Create Design in Canva
                  </CardTitle>
                  <CardDescription>Choose a design type to open Canva with preset dimensions</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Design Type</label>
                      <Select value={selectedDesignType} onValueChange={setSelectedDesignType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select design type" />
                        </SelectTrigger>
                        <SelectContent>
                          {designTypes.map(type => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name} ({type.width}x{type.height})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Design Title (Optional)</label>
                      <Input 
                        value={designTitle}
                        onChange={(e) => setDesignTitle(e.target.value)}
                        placeholder="My Awesome Design"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateCanvaDesign} 
                    disabled={loading || !selectedDesignType}
                    className="bg-gradient-to-r from-purple-500 to-pink-500"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in Canva
                  </Button>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t">
                    {designTypes.slice(0, 4).map(type => (
                      <button
                        key={type.id}
                        onClick={() => { setSelectedDesignType(type.id); handleCreateCanvaDesign(); }}
                        className="p-3 border rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all text-center"
                      >
                        <Image className="w-6 h-6 mx-auto mb-2 text-purple-500" />
                        <span className="text-xs font-medium block">{type.name}</span>
                        <span className="text-[10px] text-gray-500">{type.width}x{type.height}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Google Drive Tab */}
            <TabsContent value="google-drive">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-blue-500" />
                    Export to File
                  </CardTitle>
                  <CardDescription>Save your content as a downloadable file</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Content to Export</label>
                    <textarea
                      value={exportContent}
                      onChange={(e) => setExportContent(e.target.value)}
                      placeholder="Paste your content here (report data, keywords, content, etc.)"
                      className="w-full h-32 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Filename</label>
                      <Input 
                        value={exportFilename}
                        onChange={(e) => setExportFilename(e.target.value)}
                        placeholder="my-export"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Format</label>
                      <Select value={exportFormat} onValueChange={setExportFormat}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {exportFormats.map(format => (
                            <SelectItem key={format.id} value={format.id}>
                              {format.name} ({format.extension})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handlePrepareExport} 
                    disabled={loading || !exportContent.trim()}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Zapier Tab */}
            <TabsContent value="zapier">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="w-5 h-5 text-orange-500" />
                      Add New Webhook
                    </CardTitle>
                    <CardDescription>Connect MarkeeMix events to your Zapier workflows</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Webhook URL</label>
                        <Input 
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://hooks.zapier.com/..."
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Trigger Event</label>
                        <Select value={webhookEvent} onValueChange={setWebhookEvent}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select event" />
                          </SelectTrigger>
                          <SelectContent>
                            {zapierEvents.map(event => (
                              <SelectItem key={event.id} value={event.id}>
                                {event.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleConfigureWebhook} 
                      disabled={loading || !webhookUrl || !webhookEvent}
                      className="bg-gradient-to-r from-orange-500 to-amber-500"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Add Webhook
                    </Button>
                  </CardContent>
                </Card>

                {/* Active Webhooks */}
                <Card>
                  <CardHeader>
                    <CardTitle>Active Webhooks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {userWebhooks.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No webhooks configured yet</p>
                        <p className="text-sm">Add your first webhook above</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {userWebhooks.map(webhook => (
                          <div key={webhook.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Bell className="w-5 h-5 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {webhook.event_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </p>
                                <p className="text-xs text-gray-500 truncate max-w-[200px]">
                                  {webhook.webhook_url}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={webhook.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                                {webhook.is_active ? "Active" : "Inactive"}
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleTestWebhook(webhook.event_type)}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteWebhook(webhook.event_type)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Available Events */}
                <Card>
                  <CardHeader>
                    <CardTitle>Available Events</CardTitle>
                    <CardDescription>Events that can trigger your Zapier workflows</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {zapierEvents.map(event => (
                        <div key={event.id} className="p-3 border rounded-lg">
                          <p className="font-medium text-sm">
                            {event.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{event.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}
